import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid2,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UnitForm from "../components/UnitForm";
import UnitNumberTemplateForm from "../components/UnitNumberTemplateForm";
import UnitNumberForm from "../components/UnitNumberForm";
import { useGetMyMultiUnitPropertiesQuery } from "../../properties/propertiesApiSlice";
import { useGetUnitTypesByPropertyQuery } from "../../unitTypes/unitTypesApiSlice";
import { Link, useLocation } from "react-router-dom";
import {
  useAddNewUnitTemplateMutation,
  useDeleteUnitTemplateMutation,
} from "../../unitTemplates/api/unitTemplatesApiSlice";
import { useAddMultipleUnitsMutation } from "../api/unitsApiSlice";
import dayjs from "dayjs";

const AddUnits = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const [addNewUnitTemplate, { isLoading: isUnitTemplateSaving }] =
    useAddNewUnitTemplateMutation();
  const [deleteUnitTemplate] = useDeleteUnitTemplateMutation();
  const [addMultipleUnits, { isLoading: isMultipleUnitsSaving }] =
    useAddMultipleUnitsMutation();

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const propertyId = params.get("id") || ""; // Extracts the ID from the query string

  const [selectedProperty, setSelectedProperty] = useState(propertyId);
  const [remainingUnits, setRemainingUnits] = useState(0);
  // const [maxUnits, setMaxUnits] = useState(1); // Not in use

  const selectErrorMsg = `Select a valid choice`;
  const [propertyError, setPropertyError] = useState(false);
  const propertyRef = useRef(null);

  const [numUnitsError, setNumUnitsError] = useState(false);
  const [numUnitsErrorMsg, setNumUnitsErrorMsg] = useState("");
  const numUnitsRef = useRef(null);

  const UNIT_PREFIX = "Unit-";
  const UNIT_NUMBER = 1;

  const {
    data: properties,
    isLoading: isPropertyLoading,
    isError: isPropertyError,
    error: fetchedPropertyError,
    isSuccess: isPropertySuccess,
  } = useGetMyMultiUnitPropertiesQuery();

  const {
    // Getting data and renaming it to fetchedUnitTypes
    data: fetchedUnitTypes,
    isLoading: isUnitTypeLoading,
    isSuccess: isUnitTypeSuccess,
    isError: isUnitTypeError,
    error: unitTypeError,
  } = useGetUnitTypesByPropertyQuery(selectedProperty, {
    skip: !selectedProperty, // Skip query if no property is selected
  });

  useEffect(() => {
    if (propertyId) {
      const matchedProperty = properties?.ids.find(
        ({ key }) => key === propertyId
      );
      if (matchedProperty) {
        setSelectedProperty(propertyId); // Set the matching ID as selected
      }
    }
  }, [propertyId]);

  const handlePropertyChange = (e) => {
    setSelectedProperty(e.target.value);
  };

  useEffect(() => {
    if (selectedProperty) {
      setPropertyError(false); // Clear any previous error
      setSelectedProperty(selectedProperty); // Update the selected property

      // Get the selected property's unitCount from properties.entities
      const selectedPropertyData = properties?.entities[selectedProperty];

      if (selectedPropertyData) {
        const unitCount = selectedPropertyData.unitCount;
        const nftType = selectedPropertyData.nftType;

        let remainingUnitsCount = 1;

        if (nftType === "2") {
          remainingUnitsCount = 10 - unitCount;
          // setMaxUnits(10); // Not in use
        } else if (nftType === "3") {
          remainingUnitsCount = 100 - unitCount;
          // setMaxUnits(100); // Not in use
        }

        setNumUnitsErrorMsg("");
        setRemainingUnits(remainingUnitsCount);
      }
    }
  }, [selectedProperty, properties]);

  const handleNumUnitsChange = (e) => {
    // const numUnits = parseInt(e.target.value, 10);
    // setNumUnitsError(false);
    // setNumUnitsErrorMsg("");
    // setNumUnits(numUnits);

    // console.log(198, remainingUnits, numUnits);
    // if (remainingUnits === 0) {
    //   setNumUnitsErrorMsg("Please select a Property");
    // } else if (isNaN(numUnits) || numUnits < 1) {
    //   setNumUnitsErrorMsg("Number of units must be at least 1");
    // } else if (numUnits > remainingUnits) {
    //   setNumUnitsErrorMsg(`Number of units cannot exceed ${remainingUnits}`);
    // }

    const numUnits = parseInt(e.target.value, 10);
    setNumUnits(numUnits);
    setNumUnitsError(false);
    setNumUnitsErrorMsg("");

    if (remainingUnits === 0) {
      setNumUnitsErrorMsg("Please select a Property");
    } else if (numUnits > remainingUnits) {
      setNumUnitsErrorMsg(`Number of units cannot exceed ${remainingUnits}`);
    }
  };

  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [successUnitCount, setSuccessUnitsCount] = useState(0);
  const errRef = useRef();
  const successRef = useRef();

  // Focus if error received from server
  useEffect(() => {
    // Focus on the alert or log it once it's rendered
    if (errMsg && errRef.current) {
      errRef.current.focus();
    }
  }, [errMsg]);

  // Focus if success received from server
  useEffect(() => {
    // Focus on the alert or log it once it's rendered
    if (success && successRef.current) {
      successRef.current.focus();
    }
  }, [success]);

  const paymentFrequencyArray = [
    { key: "monthly", value: "Monthly" },
    { key: "yearly", value: "Yearly" },
    { key: "daily", value: "Daily" },
  ];

  // List of Bedrooms for dropdown menu
  const bedroomsArray = [
    { key: "studio", value: "Studio" },
    { key: "1", value: "1 Bedroom" },
    { key: "2", value: "2 Bedrooms" },
    { key: "3", value: "3 Bedrooms" },
    { key: "4", value: "4 Bedrooms" },
    { key: "5", value: "5 Bedrooms" },
    { key: "5+", value: "5+ Bedrooms" },
  ];

  // List of Bathrooms for dropdown menu
  const bathroomsArray = [
    { key: "1", value: "1" },
    { key: "2", value: "2" },
    { key: "3", value: "3" },
    { key: "4", value: "4" },
    { key: "5", value: "5" },
    { key: "5+", value: "5+" },
  ];

  // Helper function to get the display value from a key
  const getDisplayValue = (key, options) => {
    const option = options.find((opt) => opt.key === key);
    return option ? option.value : key; // Fallback to key if no match is found
  };

  // Ref to access the UnitForm methods
  const unitFormRef = useRef(null);
  const unitNumberTemplateFormRef = useRef(null);
  const unitNumberFormRef = useRef(null);

  // Template state to hold default values for a unit
  const [template, setTemplate] = useState({
    unitPrefix: UNIT_PREFIX,
    startingNumber: UNIT_NUMBER,
    property: "",
    unitType: "",
    size: "",
    bedrooms: "",
    bathrooms: "",
    price: "",
    paymentFrequency: "",
    unitImages: [],
    unitMainImage: null,
    startDate: null,
    endDate: null,
    comments: "",
  });

  const [view, setView] = useState("grid");

  // Number of units to generate with the template
  const [numUnits, setNumUnits] = useState(1);

  // The list of units generated from the template
  const [units, setUnits] = useState([]);

  // State for managing the edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // Handle template data change
  const handleTemplateDataChange = (data) => {
    setTemplate((prev) => ({
      ...prev,
      ...data,
    }));
  };

  // Generate units from the template
  const handleApplyTemplate = () => {
    // 1. Check if propertyTypeValue is in propertyTypes ids list
    if (!selectedProperty) {
      setPropertyError(true);
      propertyRef.current.focus();
      return false;
    }

    // Validate numUnits
    if (isNaN(numUnits)) {
      setNumUnitsError(true);
      setNumUnitsErrorMsg("This field is required");
      numUnitsRef.current.focus();
      return false;
    } else if (numUnits > remainingUnits) {
      setNumUnitsError(true);
      setNumUnitsErrorMsg(`Number of units cannot exceed ${remainingUnits}`);
      numUnitsRef.current.focus();
      return false; // Prevent form submission
    }

    // if (unitFormRef.current) {
    //   const isValid = unitFormRef.current.validateUserInput();
    //   if (isValid) {
    //     const templateData = unitFormRef.current.getFormData();
    //     const createdUnits = Array.from({ length: numUnits }, (_, index) => ({
    //       id: index,
    //       // unitPrefix: unitPrefix,
    //       // startingNumber: startingNumber,
    //       ...templateData, // Copy template data
    //       imagesChanged: false, // Default to false (images are same as template)
    //     }));
    //     setUnits(createdUnits);
    //   }
    // }

    const isUnitValid = unitFormRef.current.validateUserInput();
    const isUnitNumberValid =
      unitNumberTemplateFormRef.current.validateUserInput();
    if (!isUnitValid || !isUnitNumberValid) {
      // console.log("Validation failed. Please fix errors.");
      return;
    }

    // Get data from both forms
    const unitFormData = unitFormRef.current.getFormData();
    const unitNumberTemplateFormData =
      unitNumberTemplateFormRef.current.getFormData();

    // Combine form data
    const templateData = {
      ...unitFormData,
      ...unitNumberTemplateFormData,
      unitPrefix: unitNumberTemplateFormData.unitPrefix,
      startingNumber: unitNumberTemplateFormData.startingNumber,
    };

    // Create units array
    const createdUnits = Array.from({ length: numUnits }, (_, index) => ({
      id: index,
      unitNumber:
        parseInt(unitNumberTemplateFormData.startingNumber, 10) + index,
      ...templateData,
      imagesChanged: false,
    }));

    setUnits(createdUnits);
  };

  // Open the edit dialog for a specific unit
  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setEditDialogOpen(true);
  };

  // Close the edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUnit(null);
  };

  // Save changes to a unit
  const handleSaveUnit = (updatedUnit) => {
    if (unitFormRef.current && unitNumberFormRef.current) {
      const isValid = unitFormRef.current.validateUserInput(); // Validate the form
      const isUnitNumberValid = unitNumberFormRef.current.validateUserInput();

      if (isValid && isUnitNumberValid) {
        const newUnits = units.map((unit) =>
          unit.id === updatedUnit.id
            ? { ...updatedUnit, imagesChanged: true }
            : unit
        );

        setUnits(newUnits); // Update the units state
        handleCloseEditDialog(); // Close the dialog
      } else {
        console.error("Validation failed. Please fix the errors.");
      }
    }
  };

  const saveTemplate = async () => {
    const formData = new FormData();

    formData.append("property", selectedProperty);
    formData.append("unitCount", numUnits);

    formData.append("unitType", template.unitType);
    formData.append("size", template.size);
    formData.append("bedrooms", template.bedrooms);
    formData.append("bathrooms", template.bathrooms);
    formData.append("price", template.price);
    formData.append("paymentFrequency", template.paymentFrequency);
    formData.append(
      "startDate",
      template.startDate ? template.startDate.toISOString() : null
    );
    formData.append(
      "endDate",
      template.endDate ? template.endDate.toISOString() : null
    );
    formData.append("comments", template.comments);
    formData.append("prefix", template.unitPrefix);
    formData.append("startingNumber", template.startingNumber);

    formData.append("mainImage", template.unitMainImage.file);
    template.unitImages.forEach((image) => {
      formData.append("images", image.file);
    });

    // for (let [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }
    // return;

    try {
      // Trigger the mutation
      const addUnitTemplate = await addNewUnitTemplate(formData).unwrap();
      if (addUnitTemplate) {
        setSuccess(addUnitTemplate.propertyId);
        return addUnitTemplate;
      }
    } catch (err) {
      throw err; // Propagate the error
    }
  };

  const saveUnits = async (unitTemplateId) => {
    const formData = new FormData();

    // Append template ID
    formData.append("unitTemplateId", unitTemplateId); // Link to the template

    // Update units with new images if changed
    const updatedUnits = units.map((unit) => {
      // // Common properties for all units
      // const commonProperties = {
      //   unitType: unit.unitType,
      //   bedrooms: unit.bedrooms,
      //   bathrooms: unit.bathrooms,
      //   price: unit.price,
      //   paymentFrequency: unit.paymentFrequency,
      //   startDate: unit.startDate,
      //   endDate: unit.endDate,
      //   comments: unit.comments,
      // };

      if (unit.imagesChanged) {
        return {
          ...unit,
          startDate: unit.startDate ? unit.startDate.toISOString() : null,
          endDate: unit.endDate ? unit.endDate.toISOString() : null,

          unitMainImage: unit.unitMainImage,
          unitImages: unit.unitImages
            ? unit.unitImages.map((image) => image)
            : [], // Use file names
        };
      } else {
        return {
          ...unit,
          startDate: unit.startDate ? unit.startDate.toISOString() : null,
          endDate: unit.endDate ? unit.endDate.toISOString() : null,

          unitMainImage: null,
          unitImages: [],
        };
      }
    });

    // Append all units as a JSON string
    formData.append("units", JSON.stringify(updatedUnits)); // Send units as JSON

    // Append image files for units with changed images
    updatedUnits.forEach((unit, index) => {
      if (unit.imagesChanged) {
        if (unit.unitMainImage) {
          formData.append(`unit${index}_mainImage`, unit.unitMainImage.file);
        }
        unit.unitImages.forEach((image, imageIndex) => {
          formData.append(`unit${index}_image${imageIndex}`, image.file);
        });
      }
    });

    try {
      // Trigger the mutation
      const addNewUnits = await addMultipleUnits(formData).unwrap();

      if (addNewUnits) {
        setSuccessUnitsCount(addNewUnits.unitCount);
        return addNewUnits;
      }
    } catch (err) {
      throw err; // Propagate the error
    }
  };

  // Validate and save all units
  const handleSaveAllUnits = async () => {
    let allValid = true;
    units.forEach((unit) => {
      if (
        !unitFormRef.current.validateUserInput() &&
        !unitNumberFormRef.current.validateUserInput()
      ) {
        allValid = false;
      }
    });

    if (allValid) {
      let savedTemplate;
      try {
        // Step 1: Save the template
        savedTemplate = await saveTemplate();
        // console.log("Template saved successfully:", savedTemplate);

        if (!savedTemplate) {
          throw new Error("Failed to save template");
        }

        // const savedTemplate = {
        //   message: "New unitTemplate created",
        //   id: "67d40370067a6f652ed62553",
        // };

        // Step 2: Save the units
        const savedUnits = await saveUnits(savedTemplate.id);
        // console.log("Units saved successfully:", savedUnits);

        if (!savedUnits) {
          throw new Error("Failed to save units");
        }

        // console.log("Template and units saved successfully:", {
        //   template: savedTemplate,
        //   units: savedUnits,
        // });

        // Step 3: Set success message
        setSuccess(savedTemplate.propertyId); // Use template ID or any other identifier
        setErrMsg(""); // Clear any previous error message
      } catch (error) {
        console.error("Failed to save data:", error);

        // Step 4: Revert the template if saving units fails
        if (savedTemplate?.id) {
          try {
            await deleteUnitTemplate(savedTemplate.id).unwrap();
            // console.log("Template reverted successfully.");
          } catch (revertError) {
            console.error("Failed to revert template:", revertError);
          }
        }

        if (!error.status) {
          setErrMsg("No Server Response");
        } else if (error.status === 400) {
          setErrMsg(
            error.data?.message || "Invalid data. Please check your inputs."
          );
        } else if (error.status === 500) {
          setErrMsg(
            error.data?.message || "Server error. Please try again later."
          );
        } else {
          setErrMsg(error.data?.message || "An unexpected error occurred.");
        }

        setSuccess(false);
      }
    } else {
      console.error("Some units have invalid data. Please fix the errors.");
      setErrMsg("Some units have invalid data. Please fix the errors.");
      setSuccess(false);
    }
  };

  return (
    <>
      <Container sx={{ marginTop: 5, marginBottom: 3, textAlign: "center" }}>
        <Typography variant="h4" component="h1">
          Unit Template
        </Typography>
      </Container>
      <Container>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          {/* {errMsg && !success && ( */}
          {errMsg && (
            <Alert
              variant="outlined"
              severity="error"
              ref={errRef}
              tabIndex={-1}
              aria-live="assertive"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setErrMsg("")} // or your close logic
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {errMsg}
            </Alert>
          )}
          {success && (
            <Alert
              variant="outlined"
              severity="success"
              ref={successRef}
              tabIndex={-1}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setSuccess(false)} // or your close logic
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {successUnitCount} unit{successUnitCount > 1 && "s"} created.{" "}
              <Link
                to={`/my-properties/${success}`}
                style={{ color: "inherit" }}
              >
                Review & Publish
              </Link>
            </Alert>
          )}

          {/* 1. Property Title Field */}
          <FormControl
            sx={{ width: { xs: "75%", sm: 400 } }}
            error={propertyError}
            required
          >
            <InputLabel>Property</InputLabel>
            <Select
              id="property"
              label="Property"
              name="property"
              // value={selectedProperty || ""}
              value={
                // Turn the entities map into an array, then check
                Object.values(properties?.entities ?? {}).some(
                  (p) => p.id === selectedProperty
                )
                  ? selectedProperty
                  : ""
              }
              onChange={handlePropertyChange}
              inputRef={propertyRef}
              endAdornment={
                isPropertyLoading && (
                  <InputAdornment position="end" sx={{ marginLeft: -11 }}>
                    <CircularProgress size={24} />
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="">-- Select --</MenuItem>
              {isPropertySuccess ? (
                Object.values(properties.entities).map(({ id, title }) => (
                  <MenuItem key={id} value={id}>
                    {title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Please add a Property</MenuItem>
              )}
            </Select>
            {propertyError && (
              <FormHelperText error>{selectErrorMsg}</FormHelperText>
            )}
          </FormControl>

          <UnitForm
            ref={unitFormRef}
            unitTypes={fetchedUnitTypes}
            isLoading={isUnitTypeLoading}
            isError={isUnitTypeError}
            onDataChange={handleTemplateDataChange}
            parent={"Property"}
          />
          <UnitNumberTemplateForm
            ref={unitNumberTemplateFormRef}
            onDataChange={handleTemplateDataChange}
            initialData={{
              unitPrefix: template.unitPrefix,
              startingNumber: template.startingNumber,
            }}
          />

          <TextField
            type="number"
            label="Number of Units"
            id="unitCount"
            name="unitCount"
            inputRef={numUnitsRef}
            value={selectedProperty ? numUnits : ""}
            onChange={handleNumUnitsChange}
            autoComplete="off"
            required
            sx={{ width: { xs: "75%", sm: 400 } }}
            slotProps={{
              htmlInput: {
                min: 1,
                max: remainingUnits,
                onKeyDown: (e) => {
                  // Prevent "e", "E", "+", and "-" from being entered
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                },
              },
            }}
            error={numUnitsError} // Show error if limit is reached
            helperText={
              <Typography
                variant="body4"
                sx={{ color: `${numUnitsError && "error"}` }}
              >
                {numUnitsErrorMsg
                  ? numUnitsErrorMsg
                  : selectedProperty &&
                    `${
                      isNaN(numUnits) ? 0 : parseInt(numUnits, 10)
                    }/${remainingUnits}`}
              </Typography>
            }
          />

          <Button
            variant="outlined"
            onClick={handleApplyTemplate}
            sx={{ width: { xs: "75%", sm: 400 }, mb: 2 }}
            size="large"
            color="inherit"
          >
            Display Units
          </Button>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: -4.5, mb: 2 }}
          >
            Using Above Template
          </Typography>
        </Box>

        {units.length > 0 && (
          <Box sx={{ marginTop: "30px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between", // Aligns children to the edges
                alignItems: "center", // Vertically centers the children
                marginBottom: 2, // Adds some spacing below the Box
              }}
            >
              <Box sx={{ mt: 1 }}>
                <Typography variant="h5" component="h3" gutterBottom>
                  Generated Units
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => setView(view === "grid" ? "table" : "grid")}
              >
                Switch to {view === "grid" ? "Table" : "Grid"} View
              </Button>
            </Box>

            {view === "grid" ? (
              <Box>
                <Grid2
                  container
                  spacing={3}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(300px, 1fr))", // Adjust minmax as needed
                    gap: 3,
                  }} // Ensure proper stretching
                >
                  {units.map((unit) => (
                    <Grid2 xs={12} sm={6} md={4} key={unit.id}>
                      <Card
                        sx={{
                          borderRadius: "12px",
                          boxShadow: 3,
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6" component="h4" gutterBottom>
                            {/* Unit {unit.id + 1} */}
                            {unit.unitPrefix}
                            {unit.unitNumber}
                          </Typography>

                          {/* Display Main Image */}
                          {unit.unitMainImage && (
                            <Box
                              sx={{
                                marginY: "10px",
                                width: 200,
                                height: 200,
                                display: "flex",
                                // justifyContent: "center", // Centers image horizontally
                                alignItems: "center", // Centers image vertically
                              }}
                            >
                              <img
                                src={URL.createObjectURL(
                                  unit.unitMainImage.file
                                )}
                                alt=""
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid rgba(0, 0, 0, 0.23)",
                                }}
                              />
                              {unit.unitImages.length > 0 && (
                                <Typography sx={{ ml: 1 }}>
                                  +{unit.unitImages.length}
                                </Typography>
                              )}
                            </Box>
                          )}
                          <Typography variant="body1">
                            <strong>Unit Type:</strong>{" "}
                            {fetchedUnitTypes?.entities[unit.unitType]?.name ||
                              "Unknown"}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Size:</strong> {unit.size} {"sqft"}
                            {/* <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                verticalAlign: "super",
                                fontSize: "0.6rem",
                              }}
                            >
                              2
                            </Typography> */}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Bedrooms:</strong>{" "}
                            {getDisplayValue(unit.bedrooms, bedroomsArray)}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Bathrooms:</strong> {unit.bathrooms}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Price (AED):</strong> {unit.price}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Payment Frequency:</strong>{" "}
                            {getDisplayValue(
                              unit.paymentFrequency,
                              paymentFrequencyArray
                            )}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Availability Start Date:</strong>{" "}
                            {unit.startDate
                              ? // ? unit.startDate.toISOString().split("T")[0]
                                dayjs(unit.startDate).format("YYYY-MM-DD")
                              : ""}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Availability End Date:</strong>{" "}
                            {unit.endDate
                              ? // ? unit.endDate.toISOString().split("T")[0]
                                dayjs(unit.endDate).format("YYYY-MM-DD")
                              : ""}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Additional Information:</strong>{" "}
                            {unit.comments}
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => handleEditUnit(unit)}
                            sx={{ marginTop: "10px" }}
                            color="info"
                          >
                            Edit
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Unit</TableCell>
                    <TableCell>Unit Type</TableCell>
                    <TableCell>
                      Size (sqft)
                      {/* <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          verticalAlign: "super",
                          fontSize: "0.6rem",
                        }}
                      >
                        2
                      </Typography>
                      ) */}
                    </TableCell>
                    <TableCell>Bedrooms</TableCell>
                    <TableCell>Bathrooms</TableCell>
                    <TableCell>Price (AED)</TableCell>
                    <TableCell>Payment Frequency</TableCell>
                    <TableCell>Availability Start Date</TableCell>
                    <TableCell>Availability End Date</TableCell>
                    <TableCell>Additional Information</TableCell>
                    <TableCell sx={{ pl: 4.5 }}>Pictures</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {units.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell>
                        {/* {unit.id + 1} */}
                        {unit.unitPrefix}
                        {unit.unitNumber}
                      </TableCell>
                      <TableCell>
                        {fetchedUnitTypes?.entities[unit.unitType]?.name ||
                          "Unknown"}
                      </TableCell>
                      <TableCell>{unit.size}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getDisplayValue(unit.bedrooms, bedroomsArray)}
                      </TableCell>
                      <TableCell>{unit.bathrooms}</TableCell>
                      {/* <TableCell>
                      <Input
                        type="number"
                        value={unit.price}
                        onChange={(e) => {
                          const newPrice = e.target.value;
                          setUnits((prev) =>
                            prev.map((u) =>
                              u.id === unit.id ? { ...u, price: newPrice } : u
                            )
                          );
                        }}
                      />
                    </TableCell> */}
                      <TableCell>{unit.price}</TableCell>
                      <TableCell>
                        {getDisplayValue(
                          unit.paymentFrequency,
                          paymentFrequencyArray
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {unit.startDate
                          ? // ? unit.startDate.toISOString().split("T")[0]
                            dayjs(unit.startDate).format("YYYY-MM-DD")
                          : ""}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {unit.endDate
                          ? // ? unit.endDate.toISOString().split("T")[0]
                            dayjs(unit.endDate).format("YYYY-MM-DD")
                          : ""}
                      </TableCell>
                      <TableCell>{unit.comments}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <img
                            src={URL.createObjectURL(unit.unitMainImage.file)}
                            alt=""
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid rgba(0, 0, 0, 0.23)",
                            }}
                          />
                          {unit.unitImages.length > 0 && (
                            <Typography
                              sx={{
                                marginLeft: 1,
                                whiteSpace: "nowrap",
                              }}
                            >
                              +{unit.unitImages.length}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="info"
                          onClick={() => handleEditUnit(unit)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Box
              sx={{
                display: "flex",
                // flexDirection: "column",
                // alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
            >
              <Button
                // type="submit"
                variant="outlined"
                color="inherit"
                size="large"
                onClick={handleSaveAllUnits}
                disabled={isUnitTemplateSaving || isMultipleUnitsSaving}
                sx={{ width: { xs: "75%", sm: 400 }, mt: 3, mb: 5 }}
                startIcon={
                  (isUnitTemplateSaving || isMultipleUnitsSaving) && (
                    <CircularProgress size={20} sx={{ color: "inherit" }} />
                  )
                }
              >
                Save All Units
              </Button>
            </Box>
          </Box>
        )}

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth // Ensure the dialog uses the full width
          // scroll="paper" // Make the content scrollable
        >
          {/* Dialog Title */}
          <DialogTitle
            sx={{
              borderBottom: "1px solid #e0e0e0",
              // backgroundColor: "#f5f5f5"
            }}
          >
            {/* Edit Unit: {selectedUnit && selectedUnit.id + 1} */}
            Edit:{" "}
            {selectedUnit && selectedUnit.unitPrefix + selectedUnit.unitNumber}
          </DialogTitle>

          {/* Dialog Content */}
          <DialogContent dividers sx={{ padding: 3 }}>
            {selectedUnit && (
              <Box
                // component="form"
                // onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                }}
              >
                <UnitNumberForm
                  ref={unitNumberFormRef} // Pass the ref to the form
                  onDataChange={(data) => setSelectedUnit(data)}
                  initialData={selectedUnit}
                />
                <UnitForm
                  ref={unitFormRef} // Pass the ref to the form
                  unitTypes={fetchedUnitTypes}
                  // isLoading={isUnitTypeLoading}
                  // isError={isUnitTypeError}
                  onDataChange={(data) => setSelectedUnit(data)}
                  // parent={"Property"}
                  initialData={selectedUnit}
                />
              </Box>
            )}
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions
            sx={{
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#f5f5f5",
              padding: 2,
            }}
          >
            <Button onClick={handleCloseEditDialog} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={() => handleSaveUnit(selectedUnit)}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default AddUnits;
